// src/components/common/CountryCodeSelector.tsx
import React, { useState, useEffect, useRef } from 'react';
import { FiChevronDown, FiSearch } from 'react-icons/fi';

// Country data with flags, names, and codes
const countries = [
  { code: 'in', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'us', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { code: 'gb', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'ae', name: 'United Arab Emirates', dialCode: '+971', flag: '🇦🇪' },
  { code: 'au', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'ca', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'cn', name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { code: 'de', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
  { code: 'fr', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'jp', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
  { code: 'ru', name: 'Russia', dialCode: '+7', flag: '🇷🇺' },
  { code: 'sa', name: 'Saudi Arabia', dialCode: '+966', flag: '🇸🇦' },
  { code: 'sg', name: 'Singapore', dialCode: '+65', flag: '🇸🇬' },
  { code: 'za', name: 'South Africa', dialCode: '+27', flag: '🇿🇦' },
  { code: 'kr', name: 'South Korea', dialCode: '+82', flag: '🇰🇷' },
];

interface CountryCodeSelectorProps {
  value: string;
  onChange: (dialCode: string) => void;
  disabled?: boolean;
  variant?: 'default' | 'glass';
}

const CountryCodeSelector: React.FC<CountryCodeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  variant = 'glass'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Find selected country based on dial code
  const selectedCountry = countries.find(country => country.dialCode === value) || countries[0];
  
  // Filter countries based on search input
  const filteredCountries = countries.filter(country => 
    country.name.toLowerCase().includes(search.toLowerCase()) || 
    country.dialCode.includes(search)
  );
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Handle country selection
  const handleSelect = (dialCode: string) => {
    onChange(dialCode);
    setIsOpen(false);
    setSearch('');
  };
  
  return (
    <div className="relative h-full" ref={dropdownRef}>
      {/* Selected country display - matching height of phone input */}
      <button
        type="button"
        className={`
          flex items-center justify-between w-full h-full px-3
          ${variant === 'glass' ? 'glass-input' : 'bg-gray-800 border border-gray-700 text-white'}
          rounded-lg
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{ minHeight: '42px' }} // Match the standard input height
      >
        <span className="flex items-center">
          <span className="text-lg mr-1">{selectedCountry.flag}</span>
          <span className="text-white">{selectedCountry.dialCode}</span>
        </span>
        <FiChevronDown className={`text-white/50 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-50 w-60 mt-1 glass-card rounded-lg shadow-lg overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-gray-600/30">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search countries..."
                className="w-full py-2 pl-9 pr-3 rounded-lg bg-gray-700/30 text-white focus:outline-none"
                autoFocus
              />
            </div>
          </div>
          
          {/* Country list */}
          <div className="max-h-48 overflow-y-auto">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  className="flex items-center w-full px-3 py-2 hover:bg-white/10 transition-colors"
                  onClick={() => handleSelect(country.dialCode)}
                >
                  <span className="text-lg mr-2">{country.flag}</span>
                  <span className="text-white">{country.name}</span>
                  <span className="text-white/60 ml-auto">{country.dialCode}</span>
                </button>
              ))
            ) : (
              <div className="p-3 text-center text-white/60">No countries found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryCodeSelector;
